import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import EditIcon from '@mui/icons-material/Edit';
import {
  IconButton,
  lighten,
  Palette,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { common } from '@mui/material/colors';
import { FC, useState } from 'react';
import {
  DraggableProvided,
  DraggableStateSnapshot,
  DraggingStyle,
  NotDraggingStyle,
} from 'react-beautiful-dnd';

import formatNumber from '../../../../utils/formatNumber';
import Segment from '../../interfaces/Segment';
import DeleteSegmentDialog from '../DeleteRFMSegmentDialog/DeleteRFMSegmentDialog';
import RMFConfigDialog from '../RFMConfigDialog/RFMConfigDialog';
import { useTranslations } from 'use-intl';

interface RMFConfigTableRowProps {
  segments: Segment[];
  config: Segment;
  priority: number;
  draggableProvided: DraggableProvided;
  draggableStateSnapshot: DraggableStateSnapshot;
  onUpdate: (config: Segment) => Promise<void>;
  onDelete: (config: Segment) => void;
}

const getItemStyle = (
  isDragging: boolean,
  palette:Palette,
  draggableStyle?: DraggingStyle | NotDraggingStyle,
) => ({
  background: isDragging
    ? lighten(palette.primary.main, 0.9)
    : common.white,
  display: isDragging ? 'table' : 'table-row',
  borderRadius: '.5rem',
  ...draggableStyle,
});

const RMFConfigTableRow: FC<RMFConfigTableRowProps> = ({
  segments,
  config,
  priority,
  draggableProvided,
  draggableStateSnapshot,
  onUpdate,
  onDelete,
}) => {
  const t = useTranslations('rfmConfig');
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { palette } = useTheme();
  const { name, description, recency, frequency, monetary } = config;

  const handleOnDelete = () => {
    onDelete(config);
    setIsDeleting(false);
  };

  return (
    <>
      <TableRow
        ref={draggableProvided.innerRef}
        {...draggableProvided.draggableProps}
        style={getItemStyle(
          draggableStateSnapshot.isDragging,
          palette,
          draggableProvided.draggableProps.style,
        )}
      >
        <TableCell>{priority}</TableCell>
        <TableCell align='left'>
          <Tooltip title={description}>
            <Typography variant='body2'>{name}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell align='left'>
          {recency[0] !== null ? formatNumber(recency[0]) : t('infinity')}
        </TableCell>
        <TableCell align='left'>
          {recency[1] !== null ? formatNumber(recency[1]) : t('infinity')}
        </TableCell>
        <TableCell align='left'>
          {frequency[0] !== null ? formatNumber(frequency[0]) : t('infinity')}
        </TableCell>
        <TableCell align='left'>
          {frequency[1] !== null ? formatNumber(frequency[1]) : t('infinity')}
        </TableCell>
        <TableCell align='left'>
          {monetary[0] !== null ? formatNumber(monetary[0]) : t('infinity')}
        </TableCell>
        <TableCell align='left'>
          {monetary[1] !== null ? formatNumber(monetary[1]) : t('infinity')}
        </TableCell>
        <TableCell align='left'>
          <IconButton size='small' onClick={setIsEditing.bind(null, true)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </TableCell>
        <TableCell align='left'>
          <IconButton
            size='small'
            color='error'
            onClick={setIsDeleting.bind(null, true)}
          >
            <DeleteIcon fontSize='small' />
          </IconButton>
        </TableCell>
        <TableCell align='left'>
          <div {...draggableProvided.dragHandleProps}>
            <DragHandleIcon color='secondary' />
          </div>
        </TableCell>
      </TableRow>
      {isEditing && (
        <RMFConfigDialog
          segments={segments}
          config={config}
          open={isEditing}
          onUpdate={onUpdate}
          onClose={setIsEditing.bind(null, false)}
        />
      )}
      {isDeleting && (
        <DeleteSegmentDialog
          segment={config}
          onDelete={handleOnDelete}
          onClose={setIsDeleting.bind(null, false)}
        />
      )}
    </>
  );
};
export default RMFConfigTableRow;
